import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Users,
  Clock,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Megaphone,
  RefreshCw,
  FileSpreadsheet,
  Download,
  Package,
  Laptop,
  Smartphone,
  MapPin,
  Receipt,
  Plane,
  LogIn,
  LogOut,
  Navigation,
  Gift,
  PartyPopper,
  CalendarDays,
  BarChart3
} from 'lucide-react';
import { getAuthHeaders } from '../utils/api';
import CelebrationBanner from '../components/CelebrationBanner';
import CelebrationModal from '../components/CelebrationModal';
import WidgetCustomizer, { CustomizeButton, getWidgetPrefs, isWidgetEnabled } from '../components/WidgetCustomizer';
import ThoughtOfTheDay from '../components/ThoughtOfTheDay';
import { API_URL } from '../config';


const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [employeeDashboard, setEmployeeDashboard] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [mySops, setMySops] = useState([]);
  const [myAssets, setMyAssets] = useState(null);
  const [myTours, setMyTours] = useState([]);
  const [myExpenses, setMyExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [celebrationTheme, setCelebrationTheme] = useState(null);
  const [tourStatus, setTourStatus] = useState(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [tourAttendanceCheck, setTourAttendanceCheck] = useState(null);
  const [celebrationModal, setCelebrationModal] = useState(null);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [widgetPrefs, setWidgetPrefs] = useState(() => getWidgetPrefs(user?.user_id));
  const [widgetData, setWidgetData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchWidgetData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const [statsRes, empDashRes, leaveTypesRes, sopsRes, assetsRes, toursRes, expensesRes, tourStatusRes] = await Promise.all([
        fetch(`${API_URL}/dashboard/stats`, { headers: authHeaders }),
        fetch(`${API_URL}/dashboard/employee`, { headers: authHeaders }),
        fetch(`${API_URL}/leave-types`, { headers: authHeaders }),
        fetch(`${API_URL}/sop/my-sops`, { headers: authHeaders }),
        fetch(`${API_URL}/employee-assets/my-assets`, { headers: authHeaders }),
        fetch(`${API_URL}/tours/my-tours`, { headers: authHeaders }),
        fetch(`${API_URL}/expenses/my-expenses`, { headers: authHeaders }),
        fetch(`${API_URL}/travel/my-active-tour`, { headers: authHeaders })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (empDashRes.ok) {
        const empData = await empDashRes.json();
        setEmployeeDashboard(empData);
      }

      if (leaveTypesRes.ok) {
        const ltData = await leaveTypesRes.json();
        setLeaveTypes(ltData);
      }

      if (sopsRes.ok) {
        const sopsData = await sopsRes.json();
        setMySops(sopsData);
      }

      if (assetsRes.ok) {
        const assetsData = await assetsRes.json();
        setMyAssets(assetsData);
      }

      if (toursRes.ok) {
        const toursData = await toursRes.json();
        setMyTours(toursData);
      }

      if (expensesRes.ok) {
        const expensesData = await expensesRes.json();
        setMyExpenses(expensesData);
      }

      if (tourStatusRes.ok) {
        const tourData = await tourStatusRes.json();
        setTourStatus(tourData);
      }

      // HR: Check for tour employees without check-in
      if (user?.role === 'super_admin' || user?.role === 'hr_admin' || user?.role === 'hr_executive') {
        try {
          const tacRes = await fetch(`${API_URL}/travel/tour-attendance-check`, { headers: authHeaders });
          if (tacRes.ok) setTourAttendanceCheck(await tacRes.json());
        } catch {}
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWidgetData = async () => {
    try {
      const res = await fetch(`${API_URL}/dashboard/widget-data`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) setWidgetData(await res.json());
    } catch (e) {
      console.error('Widget data error:', e);
    }
  };

  const handleMarkAttendance = async (punchType) => {
    setMarkingAttendance(true);
    try {
      const response = await fetch(`${API_URL}/attendance/mark`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          punch_type: punchType,
          source: 'manual',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${punchType} marked at ${data.time}`);
        fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to mark attendance');
      }
    } catch (error) {
      toast.error('Failed to mark attendance');
    } finally {
      setMarkingAttendance(false);
    }
  };

  const handleRemoteCheckin = async (punchType) => {
    setRemoteLoading(true);
    try {
      // Use Capacitor geolocation on native, browser API on web
      let latitude, longitude;
      try {
        const { getCurrentPosition } = await import('../services/nativeServices');
        const pos = await getCurrentPosition();
        latitude = pos.lat;
        longitude = pos.lng;
      } catch (locErr) {
        if (locErr?.code === 1 || locErr?.message?.includes('permission')) {
          toast.error('Location permission denied. Please enable location in your phone settings.');
        } else {
          toast.error('Failed to get location. Please ensure GPS is enabled.');
        }
        setRemoteLoading(false);
        return;
      }
      const response = await fetch(`${API_URL}/travel/remote-check-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          punch_type: punchType,
          latitude,
          longitude,
          tour_request_id: tourStatus?.tour?.request_id
        }),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Remote ${punchType} recorded at ${data.time}`);
        fetchDashboardData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to record check-in');
      }
    } catch (error) {
      toast.error('Failed to record check-in');
    } finally {
      setRemoteLoading(false);
    }
  };

  const isHR = user?.role === 'super_admin' || user?.role === 'hr_admin' || user?.role === 'hr_executive';

  const handleMarkTourAttendance = async (employeeId, status) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(`${API_URL}/travel/mark-tour-attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({ employee_id: employeeId, date: today, status }),
      });
      if (res.ok) {
        toast.success(`Marked as ${status}`);
        setTourAttendanceCheck(prev => prev ? ({
          ...prev,
          unchecked_employees: prev.unchecked_employees.filter(e => e.employee_id !== employeeId),
          unchecked_count: prev.unchecked_count - 1,
        }) : null);
      }
    } catch { toast.error('Failed'); }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        {/* Header skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-5 w-48" />
        </div>
        
        {/* Stats skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                  <Skeleton className="h-12 w-12 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Content skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${celebrationTheme ? `celebration-theme celebration-theme-${celebrationTheme}` : ''}`} data-testid="dashboard-page">
      {/* Thought of the Day - shows once per day on first visit */}
      <ThoughtOfTheDay authHeaders={getAuthHeaders()} />

      {/* Celebration Modal - shows once per day */}
      {celebrationModal && (
        <CelebrationModal
          event={celebrationModal}
          onClose={() => {
            const today = new Date().toISOString().split('T')[0];
            localStorage.setItem(`celebration_shown_${user?.user_id}`, today);
            setCelebrationModal(null);
          }}
        />
      )}

      {/* Celebration Banner */}
      <CelebrationBanner
        onThemeDetected={setCelebrationTheme}
        onMyEvent={(myEvent) => {
          if (myEvent) {
            const today = new Date().toISOString().split('T')[0];
            const lastShown = localStorage.getItem(`celebration_shown_${user?.user_id}`);
            if (lastShown !== today) {
              setCelebrationModal(myEvent);
            }
          }
        }}
      />

      {/* Welcome Section */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 dashboard-welcome rounded-xl p-4 -mx-4 sm:-mx-0"
      >
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="section-pill mono-accent">// Dashboard</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-500 mt-1.5 text-sm">
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <div className="header-accent-line mt-3 max-w-[200px]" />
        </div>
        <CustomizeButton onClick={() => setShowCustomizer(true)} />
      </motion.div>

      {/* Widget Customizer Dialog */}
      {showCustomizer && (
        <WidgetCustomizer
          userId={user?.user_id}
          onClose={() => setShowCustomizer(false)}
          onSave={(prefs) => setWidgetPrefs(prefs)}
        />
      )}

      {/* Remote Check-in Shortcut - only for eligible employees */}
      {tourStatus?.can_remote_checkin && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent" data-testid="remote-checkin-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Navigation className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-slate-800">Remote Check-in</p>
                    <p className="text-xs text-slate-500">
                      {tourStatus?.has_active_tour
                        ? `On tour: ${tourStatus.tour?.purpose || 'Active Tour'}`
                        : tourStatus?.is_field_employee
                          ? 'Field employee — GPS check-in available'
                          : 'Remote check-in available'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {tourStatus?.todays_checkins?.length > 0 && (
                    <span className="text-xs text-slate-400 mr-2">
                      {tourStatus.todays_checkins.length} check-in{tourStatus.todays_checkins.length !== 1 ? 's' : ''} today
                    </span>
                  )}
                  <Button size="sm" onClick={() => handleRemoteCheckin('IN')} disabled={remoteLoading} data-testid="dashboard-remote-checkin-btn">
                    <LogIn className="w-3.5 h-3.5 mr-1.5" /> Clock In
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleRemoteCheckin('OUT')} disabled={remoteLoading} data-testid="dashboard-remote-checkout-btn">
                    <LogOut className="w-3.5 h-3.5 mr-1.5" /> Clock Out
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Tour Attendance Alert for HR */}
      {isHR && tourAttendanceCheck?.unchecked_employees?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="border-amber-200 bg-amber-50/50" data-testid="tour-attendance-alert">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <p className="font-semibold text-sm text-amber-800">Tour Employees Without Check-in Today ({tourAttendanceCheck.unchecked_count})</p>
              </div>
              <div className="space-y-2">
                {tourAttendanceCheck.unchecked_employees.map(emp => (
                  <div key={emp.employee_id} className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-amber-100">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{emp.name}</p>
                      <p className="text-xs text-slate-500">
                        {emp.is_field_employee ? 'Field Employee' : `Tour: ${emp.tour_purpose || ''} (${emp.tour_location || ''})`}
                      </p>
                    </div>
                    <div className="flex gap-1.5">
                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleMarkTourAttendance(emp.employee_id, 'present')} data-testid={`mark-present-${emp.employee_id}`}>
                        Present
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => handleMarkTourAttendance(emp.employee_id, 'absent')} data-testid={`mark-absent-${emp.employee_id}`}>
                        Absent
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Stats Cards - Show for HR/Admin */}
      {isHR && stats && (
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <motion.div variants={itemVariants}>
            <Card className="stat-card group" data-testid="stat-employees">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Total Employees</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_employees}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="stat-card group" data-testid="stat-present">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Present Today</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.present_today}</p>
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {stats.attendance_percentage}% attendance
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="stat-card group" data-testid="stat-leave">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">On Leave Today</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.on_leave_today}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Calendar className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="stat-card group" data-testid="stat-pending">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Pending Approvals</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.pending_leaves}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Attendance & Leave */}
        <div className="lg:col-span-8 space-y-6">
          {/* Today's Attendance Status */}
          {isWidgetEnabled(widgetPrefs, 'attendance') && (
          <Card data-testid="attendance-status-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Today&apos;s Attendance
              </CardTitle>
              <CardDescription>Your attendance status for today</CardDescription>
            </CardHeader>
            <CardContent>
              {employeeDashboard?.attendance_today ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          Status: <span className="text-emerald-600 capitalize">{employeeDashboard.attendance_today.status}</span>
                        </p>
                        <p className="text-sm text-slate-500">
                          First In: {employeeDashboard.attendance_today.first_in || 'N/A'} | 
                          Last Out: {employeeDashboard.attendance_today.last_out || 'In Progress'}
                        </p>
                      </div>
                    </div>
                    {employeeDashboard.attendance_today.total_hours && (
                      <Badge variant="secondary" className="text-lg px-4 py-1">
                        {employeeDashboard.attendance_today.total_hours} hrs
                      </Badge>
                    )}
                  </div>
                  {employeeDashboard.attendance_today.punches?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {employeeDashboard.attendance_today.punches.map((punch, idx) => (
                        <Badge key={idx} variant={punch.type === 'IN' ? 'default' : 'outline'}>
                          {punch.type} - {punch.time}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">No attendance marked today</p>
                    <p className="text-sm text-amber-600">Your attendance will be synced from the biometric system</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Leave Balance */}
          {isWidgetEnabled(widgetPrefs, 'leave_balance') && (
          <Card data-testid="leave-balance-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Leave Balance
                  </CardTitle>
                  <CardDescription>Your leave quota for this year</CardDescription>
                </div>
                <Link to="/dashboard/leave">
                  <Button variant="ghost" size="sm" className="gap-1">
                    View All <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {employeeDashboard?.leave_balance?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {employeeDashboard.leave_balance.slice(0, 4).map((balance, idx) => {
                    const leaveType = leaveTypes.find(lt => lt.leave_type_id === balance.leave_type_id);
                    const leaveName = leaveType?.name || leaveType?.code || balance.leave_type_id;
                    return (
                      <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-slate-600">{leaveName}</p>
                          <p className="text-sm font-semibold text-slate-900">
                            {balance.available} / {balance.opening_balance + balance.accrued}
                          </p>
                        </div>
                        <Progress 
                          value={(balance.available / (balance.opening_balance + balance.accrued || 1)) * 100} 
                          className="h-2"
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No leave balance data available</p>
              )}
            </CardContent>
          </Card>
          )}

          {/* Monthly Attendance Summary Widget */}
          {isWidgetEnabled(widgetPrefs, 'monthly_summary') && widgetData?.monthly_attendance && (
            <Card data-testid="monthly-summary-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  This Month's Attendance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Present', value: widgetData.monthly_attendance.present, color: 'emerald' },
                    { label: 'Absent', value: widgetData.monthly_attendance.absent, color: 'red' },
                    { label: 'On Leave', value: widgetData.monthly_attendance.leave, color: 'amber' },
                    { label: 'On Tour', value: widgetData.monthly_attendance.tour, color: 'blue' },
                  ].map((item) => (
                    <div key={item.label} className={`p-3 bg-${item.color}-50 rounded-lg text-center border border-${item.color}-100`}>
                      <p className={`text-2xl font-bold text-${item.color}-700`}>{item.value}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{item.label}</p>
                    </div>
                  ))}
                </div>
                {widgetData.monthly_attendance.late > 0 && (
                  <p className="text-xs text-amber-600 mt-2 text-center">
                    {widgetData.monthly_attendance.late} late arrival{widgetData.monthly_attendance.late !== 1 ? 's' : ''} this month
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Announcements & Quick Actions */}
        <div className="lg:col-span-4 space-y-6">
          {/* Upcoming Holidays Widget */}
          {isWidgetEnabled(widgetPrefs, 'upcoming_holidays') && widgetData?.upcoming_holidays?.length > 0 && (
            <Card data-testid="upcoming-holidays-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <CalendarDays className="w-5 h-5 text-orange-600" />
                    Upcoming Holidays
                  </CardTitle>
                  <Link to="/dashboard/holidays">
                    <Button variant="ghost" size="sm">View All</Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {widgetData.upcoming_holidays.slice(0, 4).map((h, idx) => {
                  const d = new Date(h.date + 'T00:00:00');
                  const dayName = d.toLocaleDateString('en-IN', { weekday: 'short' });
                  const dateStr = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                  return (
                    <div key={idx} className="flex items-center gap-3 p-2.5 bg-orange-50 rounded-lg border border-orange-100">
                      <div className="w-10 h-10 rounded-lg bg-orange-100 flex flex-col items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-orange-700 leading-none">{dateStr.split(' ')[0]}</span>
                        <span className="text-[10px] text-orange-500 leading-none">{dateStr.split(' ')[1]}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{h.name}</p>
                        <p className="text-xs text-slate-500">{dayName}{h.is_half_day ? ' (Half Day)' : ''}</p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Team Birthdays Widget */}
          {isWidgetEnabled(widgetPrefs, 'team_birthdays') && widgetData?.team_birthdays?.length > 0 && (
            <Card data-testid="team-birthdays-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Gift className="w-5 h-5 text-pink-500" />
                  Birthdays This Month
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {widgetData.team_birthdays.slice(0, 5).map((b, idx) => {
                  const initials = b.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?';
                  const dateStr = b.date ? new Date(`2026-${b.date}`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
                  return (
                    <div key={idx} className="flex items-center gap-3 p-2.5 bg-pink-50 rounded-lg border border-pink-100">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-pink-200 text-pink-700 text-xs font-bold">{initials}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{b.name}</p>
                        <p className="text-xs text-slate-500">{b.department}</p>
                      </div>
                      <Badge variant="outline" className="text-xs text-pink-600 border-pink-200 shrink-0">{dateStr}</Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Recent Announcements */}
          {isWidgetEnabled(widgetPrefs, 'announcements') && (
          <Card data-testid="announcements-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Megaphone className="w-5 h-5 text-primary" />
                  Announcements
                </CardTitle>
                <Link to="/dashboard/announcements">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {employeeDashboard?.recent_announcements?.length > 0 ? (
                employeeDashboard.recent_announcements.slice(0, 3).map((ann, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="font-medium text-slate-900 text-sm line-clamp-1">{ann.title}</p>
                      <Badge variant="outline" className="text-xs shrink-0">{ann.category}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2">{ann.content}</p>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4 text-sm">No announcements</p>
              )}
            </CardContent>
          </Card>
          )}

          {/* My SOPs Card */}
          {isWidgetEnabled(widgetPrefs, 'sops') && mySops.length > 0 && (
            <Card data-testid="my-sops-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                    My SOPs
                  </CardTitle>
                  <Link to="/dashboard/sop">
                    <Button variant="ghost" size="sm">View All</Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {mySops.slice(0, 3).map((sop, idx) => (
                  <div key={idx} className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 hover:bg-emerald-100 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="font-medium text-emerald-900 text-sm">{sop.title}</p>
                        {sop.description && (
                          <p className="text-xs text-emerald-600 line-clamp-1 mt-1">{sop.description}</p>
                        )}
                        <p className="text-xs text-emerald-500 mt-1">v{sop.version}</p>
                      </div>
                      {sop.file_name && (
                        <a
                          href={`${API_URL}/sop/${sop.sop_id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0"
                        >
                          <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100">
                            <Download className="w-3 h-3" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          {isWidgetEnabled(widgetPrefs, 'quick_actions') && (
          <Card className="quick-action-card" data-testid="quick-actions-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to="/dashboard/leave" className="block">
                <Button variant="outline" className="w-full justify-start gap-2" data-testid="quick-apply-leave">
                  <Calendar className="w-4 h-4" />
                  Apply for Leave
                </Button>
              </Link>
              <Link to="/dashboard/attendance" className="block">
                <Button variant="outline" className="w-full justify-start gap-2" data-testid="quick-view-attendance">
                  <Clock className="w-4 h-4" />
                  View Attendance
                </Button>
              </Link>
              <Link to="/dashboard/tours" className="block">
                <Button variant="outline" className="w-full justify-start gap-2" data-testid="quick-tour-request">
                  <Plane className="w-4 h-4" />
                  Tour Request
                </Button>
              </Link>
              <Link to="/dashboard/expenses" className="block">
                <Button variant="outline" className="w-full justify-start gap-2" data-testid="quick-expense-claim">
                  <Receipt className="w-4 h-4" />
                  Expense Claim
                </Button>
              </Link>
            </CardContent>
          </Card>
          )}

          {/* My Assets Card */}
          {isWidgetEnabled(widgetPrefs, 'assets') && myAssets && (
            <Card data-testid="my-assets-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Package className="w-5 h-5 text-blue-600" />
                  My Assets
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className={`p-3 rounded-lg text-center ${myAssets.laptop ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'}`}>
                    <Laptop className={`w-5 h-5 mx-auto mb-1 ${myAssets.laptop ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <p className="text-xs font-medium">Laptop</p>
                    <p className={`text-xs ${myAssets.laptop ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {myAssets.laptop ? 'Assigned' : 'N/A'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${myAssets.mobile_charger ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'}`}>
                    <Smartphone className={`w-5 h-5 mx-auto mb-1 ${myAssets.mobile_charger ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <p className="text-xs font-medium">Mobile</p>
                    <p className={`text-xs ${myAssets.mobile_charger ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {myAssets.mobile_charger ? 'Assigned' : 'N/A'}
                    </p>
                  </div>
                </div>
                {myAssets.sdpl_number && (
                  <div className="p-2 bg-blue-50 rounded text-xs">
                    <span className="text-blue-600 font-medium">SDPL#:</span> {myAssets.sdpl_number}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* My Tours Card */}
          {isWidgetEnabled(widgetPrefs, 'tours') && myTours.length > 0 && (
            <Card data-testid="my-tours-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <MapPin className="w-5 h-5 text-purple-600" />
                    My Tours
                  </CardTitle>
                  <Link to="/dashboard/tours">
                    <Button variant="ghost" size="sm">View All</Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {myTours.slice(0, 2).map((tour, idx) => (
                  <div key={idx} className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-purple-900 text-sm">{tour.destination}</p>
                      <Badge className={
                        tour.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        tour.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-50 text-slate-600'
                      }>
                        {tour.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-purple-600">
                      {tour.from_date} - {tour.to_date}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* My Expenses Card */}
          {isWidgetEnabled(widgetPrefs, 'expenses') && myExpenses.length > 0 && (
            <Card data-testid="my-expenses-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Receipt className="w-5 h-5 text-orange-600" />
                    My Expenses
                  </CardTitle>
                  <Link to="/dashboard/expenses">
                    <Button variant="ghost" size="sm">View All</Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {myExpenses.slice(0, 2).map((expense, idx) => (
                  <div key={idx} className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-orange-900 text-sm">{expense.category || expense.description}</p>
                      <Badge className={
                        expense.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        expense.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        expense.status === 'reimbursed' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-50 text-slate-600'
                      }>
                        {expense.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-orange-600">
                      ₹{expense.amount?.toLocaleString('en-IN')} • {expense.date}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Pending Actions for Managers/HR */}
          {(isHR || user?.role === 'manager') && stats?.pending_leaves > 0 && (
            <Card className="border-amber-200 bg-amber-50" data-testid="pending-actions-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-amber-900">Pending Approvals</p>
                    <p className="text-sm text-amber-700">{stats.pending_leaves} leave requests need your attention</p>
                  </div>
                  <Link to="/dashboard/leave">
                    <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                      Review
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
